import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { GetPatientOverviewResponse, PaymentGatewayCustomerDto, PortalUserWithPermissionsMyleDto, SubscriptionGroupMyleDto } from "@/Common/Models/Portal/MedfarPortal";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfilePortalManagementResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_GetPatientPortalProfileOverview, this.getFullAccountOverview);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_CreateSubscription, this.subscription_create);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_Subscription_ChangeManagedAccounts, this.subscription_update);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_GetSubscription, this.subscription_update);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_GetAccount, this.account_update);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_PatientRegistered, this.update_userRegistrationState);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_GetPaymentCustomer, this.update_paymentCustomer);
    }

    // -------------------------------------------------------------------------------------------------------------------------
    // - portal mgmgt ----------------------------------------------------------------------------------------------------------
    // -------------------------------------------------------------------------------------------------------------------------
    private getFullAccountOverview(response: ServerEvent, store: PatientStore): void {
        let portalModel = store.PatientPortalOverviewModels[response.PatientId];
        let overview = response.Data.Overview as GetPatientOverviewResponse;

        let errors = response.Errors as ErrorMessage[];
        if (overview != null) {
            portalModel = portalModel.set("PatientProfileOverview", overview);

            // if for some reason portal never notified myle of patient activation, do it
            if (store.PatientModels[response.PatientId] != null && store.PatientModels[response.PatientId].IndividualResponse != null) {
                let isActivated = overview.Account != null ? overview.Account.DateActivated != null : false;

                if (isActivated && store.PatientModels[response.PatientId].IndividualResponse.PortalSyncState != PortalRegistrationState.RegistrationComplete) {
                    store.ActionsPortalManagement.account_updateAccountRegistrationState(response.PatientId);
                }
            }
        }

        let patientModel = store.PatientModels[response.PatientId];

        patientModel.IndividualResponse.PortalProfileId = overview?.Profile?.PatientProfileId;
        patientModel.IndividualResponse.PortalSyncState = overview?.RegistrationState;

        store.PatientModels[response.PatientId] = patientModel;

        portalModel = portalModel.set("BackendErrors", errors);
        store.PatientPortalOverviewModels[response.PatientId] = portalModel;
        store.EmitChangeToListeners();
    }

    private subscription_create(response: ServerEvent, store: PatientStore): void {
        let model = store.PatientPortalOverviewModels[response.PatientId];
        if (model != null) {
            let errors = response.Errors as ErrorMessage[];
            model = model.set("BackendErrors", errors);

            if (errors == null || errors.length == 0) {
                let updatedSubscription: SubscriptionGroupMyleDto = response.Data.Subscription;
                let currentOverview = model.get("PatientProfileOverview");
                currentOverview.Subscription = updatedSubscription;
                model = model.set("PatientProfileOverview", currentOverview);
            }
        }
        store.PatientPortalOverviewModels[response.PatientId] = model;
    }

    /**
     * Refreshes portal subscription model
     * @param response
     * @param store
     */
    private subscription_update(response: ServerEvent, store: PatientStore): void {
        let model = store.PatientPortalOverviewModels[response.PatientId];
        if (model != null) {
            let errors = response.Errors as ErrorMessage[];
            model = model.set("BackendErrors", errors);

            if (errors == null || errors.length == 0) {
                let updatedSubscription: SubscriptionGroupMyleDto = response.Data.Subscription;
                let currentOverview = model.get("PatientProfileOverview");
                currentOverview.Subscription = updatedSubscription;
                model = model.set("PatientProfileOverview", currentOverview);
            }
        }

        store.PatientPortalOverviewModels[response.PatientId] = model;
    }

    /**
     * Refreshes portal account model
     * @param response
     * @param store
     */
    private account_update(response: ServerEvent, store: PatientStore): void {
        let model = store.PatientPortalOverviewModels[response.PatientId];
        if (model != null) {
            let errors = response.Errors as ErrorMessage[];
            model = model.set("BackendErrors", errors);

            if (errors == null || errors.length == 0) {
                let updatedAccount: PortalUserWithPermissionsMyleDto = response.Data.PortalUserAccount;
                let currentOverview = model.get("PatientProfileOverview");
                if (currentOverview)
                    currentOverview.Account = updatedAccount;
                model = model.set("PatientProfileOverview", currentOverview);
            }
        }

        store.PatientPortalOverviewModels[response.PatientId] = model;
    }

    private update_userRegistrationState(response: ServerEvent, store: PatientStore): void {
        let portalId = (store.PatientModels[response.PatientId] != null && store.PatientModels[response.PatientId].IndividualResponse != null) ? store.PatientModels[response.PatientId].IndividualResponse.PortalProfileId : null;

        if (response.Data.Success == true && portalId != null) {
            let overview = response.Data.Overview as GetPatientOverviewResponse;
            store.updatePatientPortalInfo(response.PatientId, portalId, PortalRegistrationState.RegistrationComplete, overview);
        }
    }

    private update_paymentCustomer(response: ServerEvent, store: PatientStore): void {
        let model = store.PatientPortalOverviewModels[response.PatientId];

        if (model != null) {
            let errors = response.Errors as ErrorMessage[];
            model = model.set("BackendErrors", errors);

            if (errors == null || errors.length == 0) {
                let updatedPaymentCustomer: PaymentGatewayCustomerDto = response.Data.PaymentCustomer;
                let currentOverview = model.get("PatientProfileOverview");
                currentOverview.PaymentCustomer = updatedPaymentCustomer;
                model = model.set("PatientProfileOverview", currentOverview);
            }
        }
    }
}

export { PatientProfilePortalManagementResponseHandler };
